import React from 'react'
import { HiArrowRight } from 'react-icons/hi'
import { Link } from 'react-router-dom'
import { CaseStudy } from '../../types/caseStudy'
import { Card, Heading, Text } from '../ui'

/**
 * Props interface for the CaseStudyCard component
 */
export interface CaseStudyCardProps {
  /** The case study data to display */
  caseStudy: CaseStudy
  /** Additional CSS classes */
  className?: string
  /** Whether to show the card in featured style */
  featured?: boolean
}

/**
 * Case Study Card component for displaying case study previews
 *
 * Features:
 * - Professional card layout matching project card design
 * - Key metrics display with visual emphasis
 * - Technology tags with categorization
 * - Reading time and publication date
 * - Featured case study highlighting
 * - Responsive design with mobile optimization
 * - Accessible navigation and interactions
 */
export const CaseStudyCard: React.FC<CaseStudyCardProps> = ({
  caseStudy,
  className = '',
  featured = false,
}) => {
  const {
    slug,
    title,
    subtitle,
    category,
    overview,
    keyMetrics,
    technologies,
    readingTime,
    publishDate,
    featured: isFeatured,
  } = caseStudy

  // Format category for display
  const categoryLabels: Record<string, string> = {
    enterprise: 'Enterprise Systems',
    architecture: 'Software Architecture',
    performance: 'Performance Optimization',
    'ai-integration': 'AI Integration',
    scalability: 'Scalability Solutions',
  }

  const categoryLabel = categoryLabels[category] || category

  // Format date for display
  const formattedDate = new Date(publishDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

  // Get primary technologies (limit to 6 for display)
  const primaryTechnologies = technologies.slice(0, 6)

  return (
    <Card
      variant="surface"
      padding="lg"
      className={`case-study-card ${featured || isFeatured ? 'featured' : ''} ${className}`}
      role="article"
      aria-labelledby={`case-study-title-${slug}`}
    >
      <div className="category-badge" aria-label={`Category: ${categoryLabel}`}>
        {categoryLabel}
        {(featured || isFeatured) && (
          <span className="featured-indicator">Featured</span>
        )}
      </div>

      <div className="case-study-header">
        <div className="case-study-title">
          <Heading
            as="h3"
            size="lg"
            variant="default"
            id={`case-study-title-${slug}`}
          >
            <Link
              to={`/case-studies/${slug}`}
              className="case-study-title-link"
              aria-label={`Read case study: ${title}`}
            >
              {title}
            </Link>
          </Heading>
        </div>
        {/* <div className="case-study-meta">
          <div className="meta-item">
            <HiClock size={14} aria-hidden="true" />
            <Text size="sm" variant="tertiary">
              {readingTime} read
            </Text>
          </div>
          <div className="meta-item">
            <HiCalendar size={14} aria-hidden="true" />
            <Text size="sm" variant="tertiary">
              {formattedDate}
            </Text>
          </div>
        </div> */}
      </div>

      <Text size="base" variant="secondary" className="case-study-description">
        {subtitle}
      </Text>

      <div className="case-study-overview">
        <Text
          size="sm"
          variant="tertiary"
          style={{ fontWeight: 600, marginBottom: '0.5rem' }}
        >
          Challenge & Impact:
        </Text>
        <Text size="sm" variant="tertiary" className="overview-text">
          <strong>Problem:</strong>{' '}
          {overview.problem.length > 120
            ? `${overview.problem.substring(0, 120)}...`
            : overview.problem}
        </Text>
        <Text size="sm" variant="tertiary" className="overview-text">
          <strong>Result:</strong>{' '}
          {overview.impact.length > 120
            ? `${overview.impact.substring(0, 120)}...`
            : overview.impact}
        </Text>
      </div>

      {/* Key Metrics */}
      {keyMetrics.length > 0 && (
        <div className="case-study-metrics">
          <Text
            size="sm"
            variant="tertiary"
            style={{ fontWeight: 600, marginBottom: '0.5rem' }}
          >
            Key Results:
          </Text>
          <div className="metrics-list">
            {keyMetrics.slice(0, 3).map((metric, index) => (
              <div key={index} className="metric-item">
                <span className="metric-value">{metric.value}</span>
                <span className="metric-label">{metric.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="case-study-footer">
        <div
          className="tech-stack"
          role="group"
          aria-label={`Technologies used in ${title}`}
        >
          {primaryTechnologies.map((tech, index) => (
            <span key={index} className="tech-tag" role="text">
              {tech.name}
            </span>
          ))}
          {technologies.length > 6 && (
            <span className="tech-tag tech-tag-more">
              +{technologies.length - 6} more
            </span>
          )}
        </div>

        <Link
          to={`/case-studies/${slug}`}
          className="case-study-read-more"
          aria-label={`Read full case study: ${title}`}
        >
          Read Case Study
          <HiArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>
    </Card>
  )
}

export default CaseStudyCard
